/**
 * @menzioni degli immobili (§1.ter, P1).
 *
 * Scrivere "@san" e scegliere dall'elenco elimina l'ambiguità in partenza:
 * invece di far chiedere a Otto *quale* casa di Sanremo, l'host lo dice
 * mentre scrive. Un giro di conversazione in meno su ogni comando.
 *
 * Il nome scelto entra fra virgolette, che è la forma che l'agente riconosce
 * meglio e che resta leggibile nella trascrizione.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ListingView } from "@otto/shared";
import { Thumb, cx } from "./primitives.tsx";

export interface MentionState {
  /** Testo digitato dopo la chiocciola. */
  query: string;
  start: number;
  end: number;
}

/**
 * Trova una menzione aperta alla posizione del cursore.
 *
 * Cerca all'indietro dalla posizione del cursore fino alla chiocciola più
 * vicina, fermandosi su uno spazio: "@san remo" non è una menzione di
 * "san remo", è una menzione di "san" seguita da altro testo.
 */
export function findMention(text: string, caret: number): MentionState | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;

  const query = before.slice(at + 1);
  // Una menzione non contiene spazi né a capo, e non è lunga come una frase.
  if (/[\s\n]/.test(query) || query.length > 30) return null;
  // La chiocciola dev'essere a inizio parola, non dentro un'email.
  const previous = at > 0 ? before[at - 1] : " ";
  if (previous && !/[\s\n(]/.test(previous)) return null;

  return { query, start: at, end: caret };
}

export function matchListings(
  listings: ListingView[],
  query: string,
  limit = 6,
): ListingView[] {
  const q = query.trim().toLowerCase();
  if (!q) return listings.slice(0, limit);

  const scored = listings
    .map((listing) => {
      const nickname = listing.nickname.toLowerCase();
      const city = (listing.city ?? "").toLowerCase();
      // Chi comincia con quello che stai scrivendo viene prima.
      if (nickname.startsWith(q)) return { listing, score: 0 };
      if (city.startsWith(q)) return { listing, score: 1 };
      if (nickname.includes(q)) return { listing, score: 2 };
      if (city.includes(q)) return { listing, score: 3 };
      return null;
    })
    .filter(
      (entry): entry is { listing: ListingView; score: number } =>
        entry !== null,
    )
    .sort((a, b) => a.score - b.score);

  return scored.slice(0, limit).map((entry) => entry.listing);
}

export function applyMention(
  text: string,
  mention: MentionState,
  nickname: string,
): string {
  return `${text.slice(0, mention.start)}"${nickname}" ${text.slice(mention.end)}`;
}

export function MentionPopup({
  listings,
  query,
  onPick,
  onDismiss,
}: {
  listings: ListingView[];
  query: string;
  onPick: (listing: ListingView) => void;
  onDismiss: () => void;
}) {
  const [active, setActive] = useState(0);
  const matches = useMemo(
    () => matchListings(listings, query),
    [listings, query],
  );
  const container = useRef<HTMLDivElement | null>(null);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (matches.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((value) => (value + 1) % matches.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActive((value) => (value - 1 + matches.length) % matches.length);
      } else if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const chosen = matches[active];
        if (chosen) onPick(chosen);
      } else if (event.key === "Escape") {
        onDismiss();
      }
    };

    // In cattura: deve arrivare prima del gestore dell'invio della textarea,
    // altrimenti Invio manderebbe il messaggio invece di scegliere la casa.
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [matches, active, onPick, onDismiss]);

  if (matches.length === 0) return null;

  return (
    <div
      ref={container}
      role="listbox"
      aria-label="Immobili"
      className="animate-rise scroll-soft absolute bottom-full left-0 right-0 mb-2 max-h-[260px] overflow-y-auto rounded-card-lg bg-surface p-1.5 shadow-lift ring-1 ring-line"
    >
      {matches.map((listing, index) => (
        <button
          key={listing.id}
          type="button"
          role="option"
          aria-selected={index === active}
          onMouseEnter={() => setActive(index)}
          onClick={() => onPick(listing)}
          className={cx(
            "flex w-full items-center gap-2.5 rounded-card p-2 text-left transition-colors duration-quick",
            index === active ? "bg-accent-soft" : "hover:bg-surface-muted",
          )}
        >
          <Thumb src={listing.photoUrl} alt={listing.nickname} size={32} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold text-ink">
              {listing.nickname}
            </span>
            {listing.city ? (
              <span className="block truncate text-[12px] text-ink-faint">
                {listing.city}
              </span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  );
}
